// =============================================================================
// ui/tabs/DangerZone.tsx — My Account → Danger zone.
// -----------------------------------------------------------------------------
// The one irreversible action in the product, and it is deliberately not
// immediate: pressing delete SCHEDULES the account for erasure thirty days out.
// Nothing is removed until a cron job runs past that date. Until then the whole
// account is intact and one button puts it back.
//
// ── 🚨 IF A DELETION IS ALREADY PENDING, THIS CARD SAYS SO AND OFFERS RESTORE ──
//
// The old client surfaces a pending deletion ONLY as a modal at sign-in. Choose
// "leave it scheduled" there and the app gives you no way to find it again short
// of signing out and back in — while a thirty-day clock runs on everything you
// own.
//
// The Danger Zone is the one place a person would look. So it reads the pending
// state, and when there is one it replaces the delete button entirely: there is
// nothing useful about offering to schedule a deletion that is already
// scheduled.
//
// ── ⚠ THE DATE IS READ, NEVER COMPUTED FROM NOW ──
//
// See `domain/accountDeletion.ts`. Every deadline on this screen comes from the
// stored `requested_at`.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from '../state/supabase';
import {
  loadPendingDeletion, restoreAccount, scheduleDeletion, type PendingDeletion,
} from '../../io/sync/accountDeletion';
import {
  DELETE_CONFIRM_WORD, DELETION_GRACE_DAYS, daysUntilPurge, formatPurgeDate,
  validateDeleteRequest,
} from '../../selectors/accountDeletion';
import { reportFailure } from '../../io/diagnostics/errorLog';

export function DangerZoneCard({ email }: { email: string }) {
  const { sb, userId } = useSupabase();
  const [pending, setPending] = useState<PendingDeletion | null>(null);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [readError, setReadError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setPhase('loading');
    setReadError(null);
    try {
      setPending(await loadPendingDeletion(sb));
      setPhase('ready');
    } catch (e) {
      setReadError(
        reportFailure('pending deletion', e, 'Couldn’t check your account status.'),
      );
      setPhase('failed');
    }
  }, [sb]);

  useEffect(() => { void refresh(); }, [refresh]);

  const restore = async () => {
    setBusy(true);
    setActionError(null);
    const { error } = await restoreAccount(sb, userId);
    setBusy(false);
    if (error) { setActionError(error); return; }
    setPending(null);
  };

  return (
    <section className="card dz-card" data-testid="danger-zone">
      <h3 className="red">Danger zone</h3>

      {phase === 'loading' ? (
        <p className="muted">Checking your account status…</p>
      ) : phase === 'failed' ? (
        // ⚠ NOT "no deletion pending". Saying that wrongly means a user whose
        // account is counting down is never told.
        <p className="muted" role="alert">
          Couldn’t check whether a deletion is scheduled. {readError}{' '}
          <button type="button" className="btn" onClick={() => void refresh()}>Try again</button>
        </p>
      ) : pending ? (
        <PendingNotice
          pending={pending}
          busy={busy}
          error={actionError}
          onRestore={() => void restore()}
        />
      ) : (
        <>
          <p className="muted">
            Permanently delete your account and all your data.{' '}
            <b>This cannot be undone</b> once the {DELETION_GRACE_DAYS}-day window
            has passed. Download your data first if you might want it.
          </p>
          {actionError ? <p className="red" role="alert">{actionError}</p> : null}
          <button type="button" className="btn danger" onClick={() => setModalOpen(true)}>
            Delete my account
          </button>
        </>
      )}

      {modalOpen ? (
        <DeleteAccountModal
          email={email}
          onClose={() => setModalOpen(false)}
          onScheduled={(p) => { setModalOpen(false); setPending(p); }}
        />
      ) : null}
    </section>
  );
}

/**
 * ⚠ THE COUNTDOWN IS THE HEADLINE. A pending deletion is not a setting — it is
 * a deadline, and the number of days left is what someone needs first.
 */
function PendingNotice(
  { pending, busy, error, onRestore }: {
    pending: PendingDeletion; busy: boolean; error: string | null; onRestore: () => void;
  },
) {
  const when = formatPurgeDate(pending.requestedAt);
  const left = daysUntilPurge(pending.requestedAt, new Date());

  return (
    <div className="dz-pending">
      <p className="red">
        <b>Your account is scheduled for permanent deletion{when ? ` on ${when}` : ''}.</b>
      </p>
      <p className="muted">
        {left === null
          ? 'Everything is still here.'
          : left === 0
            // ⚠ The last-day case says TODAY rather than "0 days left", which
            // reads as though it has already happened.
            ? 'The window closes today — everything is still here until the purge runs.'
            : `${left} day${left === 1 ? '' : 's'} left. Everything is still here — `
              + 'every trade, journal entry, screenshot and setting.'}
        {' '}Restoring cancels the deletion completely and nothing is lost.
      </p>
      {error ? <p className="red" role="alert">{error}</p> : null}
      <button type="button" className="btn primary" disabled={busy} onClick={onRestore}>
        {busy ? 'Restoring…' : 'Restore my account'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The modal
// ---------------------------------------------------------------------------

function DeleteAccountModal(
  { email, onClose, onScheduled }: {
    email: string; onClose: () => void; onScheduled: (p: PendingDeletion) => void;
  },
) {
  const { sb, userId } = useSupabase();
  const [word, setWord] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<{ text: string; bad: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<PendingDeletion | null>(null);

  const submit = async () => {
    const problem = validateDeleteRequest(word, password);
    if (problem) { setStatus({ text: problem, bad: true }); return; }

    setBusy(true);
    setStatus({ text: 'Verifying…', bad: false });
    // ⚠ Same re-authentication as every other security action. Without it,
    // someone at an unlocked machine can start a thirty-day countdown on a
    // trader's entire record.
    const { error: authError } = await sb.auth.signInWithPassword({ email, password });
    if (authError) {
      setBusy(false);
      setStatus({ text: 'Password is incorrect.', bad: true });
      return;
    }

    setStatus({ text: 'Scheduling…', bad: false });
    const res = await scheduleDeletion(sb, userId);
    setBusy(false);
    if (res.error || !res.pending) {
      setStatus({ text: res.error ?? 'Could not schedule the deletion.', bad: true });
      return;
    }
    setDone(res.pending);
  };

  if (done) return <ScheduledConfirmation pending={done} onFinish={() => onScheduled(done)} />;

  const when = formatPurgeDate(new Date().toISOString());

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Delete account">
      <div className="modal">
        <header className="modal-head">
          <b>Delete account</b>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>×</button>
        </header>

        <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <div className="modal-body">
            <div className="dz-warning">
              <b>This schedules your account for permanent deletion.</b>{' '}
              You’ll have <b>{DELETION_GRACE_DAYS} days</b> to change your mind —
              sign back in{when ? ` before ${when}` : ''} and restore it, and
              everything is exactly as you left it. After that date every trade,
              journal entry, screenshot, account, pattern and setting is erased
              and cannot be recovered. Consider downloading your data first.
            </div>

            <label className="field">
              <span className="field-label">
                Type <b>{DELETE_CONFIRM_WORD}</b> to confirm
              </span>
              <input type="text" autoComplete="off" value={word} disabled={busy}
                onChange={(e) => setWord(e.target.value)} />
            </label>

            <label className="field">
              <span className="field-label">Your password</span>
              <input type="password" autoComplete="current-password" value={password}
                disabled={busy} onChange={(e) => setPassword(e.target.value)} />
            </label>

            {status ? (
              <p className={`acct-status${status.bad ? ' red' : ' muted'}`}
                role={status.bad ? 'alert' : 'status'}>{status.text}</p>
            ) : <p className="acct-status" />}
          </div>

          <footer className="modal-foot">
            <span className="grow" />
            <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn danger" disabled={busy}>
              {busy ? 'Working…' : 'Schedule deletion'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

/**
 * ⚠ THE OLD CLIENT SIGNS THE USER OUT HERE. This does not.
 *
 * Signing out immediately is what turns a recoverable state into a frightening
 * one: the app vanishes, and the only route back to the Restore button is
 * remembering the password and signing in again. Nothing about the schedule
 * requires the session to end — the row is written and the cron job is what
 * matters. Staying signed in leaves Restore one click away on this very card.
 */
function ScheduledConfirmation(
  { pending, onFinish }: { pending: PendingDeletion; onFinish: () => void },
) {
  const when = formatPurgeDate(pending.requestedAt);
  return (
    <div className="modal-scrim" role="dialog" aria-modal="true"
      aria-label="Account scheduled for deletion">
      <div className="modal">
        <header className="modal-head"><b>Account scheduled for deletion</b></header>
        <div className="modal-body">
          <div className="dz-warning">
            Your account is <b>scheduled for permanent deletion{when ? ` on ${when}` : ''}</b>.
            Everything is preserved until then.
          </div>
          <p className="muted">
            You are still signed in, and nothing has changed yet. To cancel, press
            <b> Restore my account</b> in the Danger zone — it is there whenever
            you want it, and signing back in will also offer it.
          </p>
        </div>
        <footer className="modal-foot">
          <span className="grow" />
          <button type="button" className="btn primary" onClick={onFinish}>Close</button>
        </footer>
      </div>
    </div>
  );
}
